import PageSeo from '../seo/PageSeo';
import { useInitialData } from '../seo/InitialData';
import React, { useMemo, useState } from 'react'
import useCatalogQuery from '../hooks/useCatalogQuery';
import { filterLinks, pageOf, sortBy, useArchive } from '../api/archive';
import FilterOptionsScroll from '../components/FilterOptionsScroll'
import LinksRow from '../components/LinksRow'
import Pagination from '../components/Pagination';
import { getActiveLinksFilterCount } from './linksUtils';

export default function Links() {
  const initial = useInitialData();
  const { archive, error: archiveError, retry: retryArchive } = useArchive();
  const catalog = useCatalogQuery();
  const selectedManufacturers = catalog.manufacturers;
  const [filtersOpen, setFiltersOpen] = useState(false);
  const page = catalog.page;
  const pageSize = 24;
  const sortOrder = catalog.sort;
  const searchQuery = catalog.query;
  const activeFilterCount = getActiveLinksFilterCount(searchQuery, selectedManufacturers);
  const sourceEntries = useMemo(() => archive?.links || initial.entries || [], [archive, initial.entries]);
  const filterManufacturer = useMemo(() => archive?.manufacturers || initial.manufacturers || [], [archive, initial.manufacturers]);
  const resultPage = useMemo(() => pageOf(sortBy(filterLinks(sourceEntries, { query: searchQuery, manufacturers: selectedManufacturers }), 'Name', sortOrder), page, pageSize), [sourceEntries, searchQuery, selectedManufacturers, sortOrder, page, pageSize]);
  const data = resultPage.entries;
  const totalPages = resultPage.totalPages;
  const totalIteams = resultPage.total;
  const isLoading = !archive && !initial.entries;
  const hasError = Boolean(archiveError && !initial.entries);
  const filtersUnavailable = Boolean(archiveError && !initial.manufacturers);

  const handleSearchChange = (event) => catalog.changeSearch(event.target.value);
  const handleManufacturerChange = (id, checked) => catalog.update({
    maker: (checked ? [...selectedManufacturers, id] : selectedManufacturers.filter((value) => value !== id)).join(','),
    q: catalog.input.trim(), page: null,
  });
  const handleSortOrderChange = (order) => catalog.update({ sort: order === 'ASC' ? null : order, q: catalog.input.trim(), page: null });
  const clearFilters = catalog.clear;

  const pageUrl = (value) => {
    const next = new URLSearchParams(catalog.params);
    if (value === 1) next.delete('page');
    else next.set('page', value);
    return `/links${next.size ? `?${next}` : ''}`;
  };

  return (
    <section className="retro-catalog-page retro-links-page">
      <PageSeo />
      <div className="retro-catalog-inner">
        <header className="retro-catalog-hero">
          <div>
            <h1>Linkoteka</h1>
            <p>Adresy do archiwów, forów, dokumentacji i projektów powiązanych ze starymi komputerami.</p>
          </div>

          <div className="retro-catalog-status">
            <span>{totalIteams}</span>
            <p>adresów</p>
          </div>
        </header>

        <div className="retro-catalog-toolbar">
          <label className="retro-catalog-search">
            <i className="feather-search" aria-hidden="true"></i>
            <input
              type="search"
              value={catalog.input}
              onChange={handleSearchChange}
              placeholder="Szukaj: Atari, forum, schematy, archiwum..."
              aria-label="Szukaj w linkotece"
              maxLength={160}
            />
          </label>

          <div className="retro-sort-control" aria-label="Sortowanie linków">
            <button
              className={sortOrder === 'ASC' ? 'active' : ''}
              type="button"
              onClick={() => handleSortOrderChange('ASC')}
            >
              A-Z
            </button>
            <button
              className={sortOrder === 'DESC' ? 'active' : ''}
              type="button"
              onClick={() => handleSortOrderChange('DESC')}
            >
              Z-A
            </button>
          </div>
        </div>

        <button className="retro-mobile-filters retro-button retro-button-secondary" type="button" aria-expanded={filtersOpen} aria-controls="link-filters" onClick={() => setFiltersOpen(!filtersOpen)}>
          {filtersOpen ? 'Schowaj filtry' : 'Pokaż filtry'}{activeFilterCount ? ` (${activeFilterCount})` : ''}
        </button>
        <div className="retro-catalog-layout retro-links-layout">
          <aside id="link-filters" className={`retro-filter-panel ${filtersOpen ? 'is-open' : ''}`}>
            <div className="retro-filter-header">
              <div>
                <h2>Filtry</h2>
                <p>{activeFilterCount > 0 ? `Aktywne filtry: ${activeFilterCount}` : 'Brak aktywnych'}</p>
              </div>
              {activeFilterCount > 0 && (
                <button type="button" onClick={clearFilters}>Wyczyść</button>
              )}
            </div>

            <div className="retro-filter-group">
              <h3>Producenci</h3>
              <FilterOptionsScroll>
                {filtersUnavailable && (
                  <div className="retro-filter-unavailable">Filtry producentów są chwilowo niedostępne.</div>
                )}
                {filterManufacturer.map((manufacturer) => (
                  <label className="retro-filter-check" key={manufacturer.id}>
                    <input
                      type="checkbox"
                      checked={selectedManufacturers.includes(manufacturer.id)}
                      onChange={(event) => handleManufacturerChange(manufacturer.id, event.target.checked)}
                    />
                    <span>{manufacturer.Name}</span>
                  </label>
                ))}
              </FilterOptionsScroll>
            </div>
          </aside>

          <div className="retro-catalog-results">
            <div className="retro-results-header">
              <h2>Adresy</h2>
              <p role="status">{isLoading ? 'Wyszukiwanie…' : `Wyniki: ${totalIteams}`}</p>
            </div>

            {hasError && <div className="retro-catalog-empty">Nie udało się pobrać pliku archiwum. <button className="retro-button retro-button-secondary" onClick={retryArchive}>Spróbuj ponownie</button></div>}

            {!hasError && !isLoading && data.length === 0 && (
              <div className="retro-catalog-empty">Brak linków dla wybranych filtrów.</div>
            )}

            {!hasError && !isLoading && data.length > 0 && (
              <LinksRow linksArray={data} />
            )}

            <Pagination page={page} totalPages={totalPages} pageHref={pageUrl} ariaLabel="Stronicowanie linków" className="retro-catalog-pagination" />
          </div>
        </div>
      </div>
    </section>
  )
}
